import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UpdateCustomProductRequest } from '../../api';
import { APIMessages } from '../../api/models/api-shared.models';
import { SearchCacheEntry, TypeAheadResponse } from '../../api/models/product-catalog.models';
import {
  ProductListCatalogProduct,
  ProductListSearchResult,
  SearchProductListCatalogRequest,
  SearchProductListCatalogResultDetail,
} from '../../api/models/product-list-search.models';
import { normalizeProductKey, updateProductCustomAttributesFromPayload } from '../../helpers/general/product';
import { createPagedEntityAdapter, EntityPageLoading, PagedEntityState } from '../paged-entity-adapter';
import { RootState } from '../store';

const productsAdapter = createPagedEntityAdapter<ProductListCatalogProduct>({
  selectId: (product: ProductListCatalogProduct) => normalizeProductKey(product.Product.ProductKey),
});

export interface RecipeSearchState {
  apiRequest?: SearchProductListCatalogRequest;
  productSearchResult?: ProductListSearchResult;
  products: PagedEntityState<ProductListCatalogProduct>;

  searchCache?: SearchCacheEntry[];
  typeAheadResults: TypeAheadResponse | undefined;
  typeAheadAPIMessages?: APIMessages;
}

const initialState: RecipeSearchState = {
  productSearchResult: undefined,
  products: productsAdapter.getInitialState(true),
  typeAheadResults: undefined,
  typeAheadAPIMessages: undefined,
  searchCache: undefined,
};

export const recipeSearchSlice = createSlice({
  name: 'recipeSearch',
  initialState: initialState,
  reducers: {
    resetProductSearchState: (state: RecipeSearchState) => {
      // Resets the search state (leaves Typeahead state alone)
      state.apiRequest = initialState.apiRequest;
      state.productSearchResult = initialState.productSearchResult;
      state.products = initialState.products;
    },
    resetProductSearchResults: (state: RecipeSearchState) => {
      state.apiRequest = initialState.apiRequest;
      state.productSearchResult = initialState.productSearchResult;
      state.products = initialState.products;
    },
    setProductSearchResults: (
      state: RecipeSearchState,
      action: PayloadAction<{
        request: SearchProductListCatalogRequest;
        result: SearchProductListCatalogResultDetail;
      }>
    ) => {
      const { request, result } = action.payload;
      const catalogProducts = result.CatalogProducts;

      const productSearchResult: ProductListSearchResult = {
        Skip: result.Skip,
        PageSize: result.PageSize,
        QueryText: result.QueryText,
        TotalNumberOfItems: result.CatalogProducts.length,
        Facets: result.Facets,
        HasLoadMore: result.HasLoadMore,
        pageIndex: result.pageIndex,
      };

      state.apiRequest = request;
      state.productSearchResult = productSearchResult;

      productsAdapter.setLoadedPage(state.products, {
        pageIndex: result.pageIndex,
        entities: catalogProducts,
      });
    },
    setPageLoaded: (
      state: RecipeSearchState,
      action: PayloadAction<{ pageIndex: number; catalogProducts: ProductListCatalogProduct[] }>
    ) => {
      productsAdapter.setLoadedPage(state.products, {
        pageIndex: action.payload.pageIndex,
        entities: action.payload.catalogProducts,
      });
    },
    setPageLoading: (state: RecipeSearchState, action: PayloadAction<EntityPageLoading>) => {
      productsAdapter.setLoadingPage(state.products, action.payload);
    },
    setTypeAheadAPIMessages: (state: RecipeSearchState, action: PayloadAction<APIMessages>) => {
      state.typeAheadAPIMessages = action.payload;
    },
    setTypeAheadResults: (state: RecipeSearchState, action: PayloadAction<TypeAheadResponse>) => {
      state.typeAheadResults = action.payload;
    },
    setSearchCache: (state: RecipeSearchState, action: PayloadAction<SearchCacheEntry[]>) => {
      state.searchCache = action.payload;
    },
    updateProductCustomAttributes: (state: RecipeSearchState, action: PayloadAction<UpdateCustomProductRequest>) => {
      const product = state.products.entities[normalizeProductKey(action.payload.ProductKey)]?.Product;
      if (!product) return;

      updateProductCustomAttributesFromPayload(product, action.payload);
    },
  },
});

export const {
  selectAll: selectAllRecipeSearchProducts,
  selectById: selectRecipeSearchProduct,
  selectIsLoading: selectRecipeSearchProductListIsLoading,
  selectByPage: selectRecipeSearchProductsByPage,
  selectTotal: selectRecipeSearchTotalProducts,
} = productsAdapter.getSelectors<RootState>((state) => state.recipeSearch.products);
